import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./history-card.module.scss";
import plusIcon from "../../../../images/plus.svg";
import { saveHistoryComment } from "../../../../services/api";
import { setLogComment } from "../../../../services/reducers/master-plan-modification";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { Roles } from "../../../../types/roles";
import { FormattedHistoryLog } from "../history-log";

interface HistoryCardProps {
  data: FormattedHistoryLog;
  documentId: number;
}

const MAX_LENGTH = 120;

const HistoryCard: FC<HistoryCardProps> = ({ data, documentId }) => {
  const { t } = useTranslation();
  const { logComment } = useAppSelector(
    (state) => state.masterPlanModification,
  );
  const savedComment = logComment[documentId]?.[data.id] || data.comment || "";

  const [isEditing, setIsEditing] = useState(false);
  const [comment, setComment] = useState(savedComment);
  const [tempComment, setTempComment] = useState(savedComment);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const canEditComment =
    user?.userName === data.userName &&
    user?.roles.some(
      (role: string) =>
        role === Roles.KrookieApprover || role === Roles.KrookieCreator,
    );

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_LENGTH) {
      setTempComment(e.target.value);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }
  };

  const handleBlur = async () => {
    setIsEditing(false);
    setComment(tempComment);
    dispatch(
      setLogComment({
        documentId: documentId,
        recordId: data.id,
        comment: tempComment,
      }),
    );
    if (!data.isGenerated) {
      try {
        await saveHistoryComment(data.id, tempComment);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleEditComment = () => {
    if (canEditComment) {
      setIsEditing(true);
    }
  };

  return (
    <div className={styles.historyCard}>
      <div className={styles.info}>
        <span className={styles.title}>{data.cardTitle}</span>
        <div className={styles.row}>
          <span className={styles.label}>{t("history-card.dateLabel")}</span>
          <span className={styles.value}>{data.formattedDateTime}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>{t("history-card.drafterLabel")}</span>
          <span className={styles.value}>{data.userName}</span>
        </div>
        {comment && !isEditing && (
          <div
            style={{ cursor: canEditComment ? "pointer" : "default" }}
            onClick={handleEditComment}
            className={styles.row}
          >
            <span className={styles.label}>
              {t("history-card.commentLabel")}
            </span>
            <span className={styles.value}>{comment}</span>
          </div>
        )}
      </div>
      {isEditing ? (
        <div className={styles.textareaWrapper}>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder={t("history-card.commentPlaceholder")}
            value={tempComment}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <div className={styles.counter}>
            {tempComment.length} / {MAX_LENGTH}
          </div>
        </div>
      ) : (
        !comment &&
        canEditComment && (
          <button
            className={styles.commentBtn}
            onClick={() => setIsEditing(true)}
          >
            <img src={plusIcon} alt={t("history-card.alt.addComment")} />{" "}
            {t("history-card.addComment")}
          </button>
        )
      )}
    </div>
  );
};

export default HistoryCard;
